import { useState } from 'react';

import { allowedFileTypes } from '../../../../../../../../../../backend/src/case/doc/allowed-extensions';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { DocumentTypeFilter } from '@/new/components/doc-type-filter';
import { FilterModalContent } from '@/new/components/filter-modal-content';
import { FilterSwitch } from '@/new/components/filter-switch';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';
import type { DocFilters } from '@/types';

const fileExtensionOptions = allowedFileTypes
  .map((fileType) => {
    const extension = fileType.replace('.', '').toLowerCase();
    return extension.toUpperCase();
  })
  .sort((a, b) => a.localeCompare(b));

export const DocumentFiltersPopup = ({
  activeFilters,
  setFilters,
  handleClose,
  allDocumentTypes,
  docThemes,
  goToPage,
}: {
  activeFilters: DocFilters;
  setFilters: (v: DocFilters) => void;
  handleClose: () => void;
  allDocumentTypes: string[];
  docThemes: string[];
  goToPage?: (value: number) => void;
}) => {
  const [processedFilters, setProcessedFilters] = useState<string | undefined>(
    activeFilters.processed === true ? 'true' : activeFilters.processed === false ? 'false' : undefined,
  );
  const [extensionFilters, setExtensionFilters] = useState<string[]>(
    activeFilters.extensions?.map((ext) => ext.toUpperCase()) || [],
  );
  const [themeFilters, setThemeFilters] = useState<string[]>(activeFilters.documentThemes ?? []);
  const [startDate, setStartDate] = useState<Date | null>(
    activeFilters.startDate ? new Date(activeFilters.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(activeFilters.endDate ? new Date(activeFilters.endDate) : null);
  const [documentTypeFilters, setDocumentTypeFilters] = useState<string[]>(
    activeFilters.documentTypes?.map((type) => type.toUpperCase()) || [],
  );
  const [hasKeyFactsFilter, setHasKeyFactsFilter] = useState<boolean | null | undefined>(activeFilters.hasKeyFacts);
  const [hasRelevantFactsFilter, setHasRelevantFactsFilter] = useState<boolean | null | undefined>(
    activeFilters.hasRelevantFacts,
  );

  const handleSubmit = () => {
    const newFilters: DocFilters = {
      processed: processedFilters === 'true' ? true : processedFilters === 'false' ? false : undefined,
      extensions: extensionFilters.length > 0 ? extensionFilters : undefined,
      startDate: startDate?.toISOString() || null,
      endDate: endDate?.toISOString() || null,
      documentTypes: documentTypeFilters.length > 0 ? documentTypeFilters : undefined,
      documentThemes: themeFilters,
      hasKeyFacts: hasKeyFactsFilter,
      hasRelevantFacts: hasRelevantFactsFilter,
    };

    trackCustomEvent(TrackingEvents.ApplyFiltersOnDocumentsPage);
    setFilters(newFilters);
    handleClose();
  };
  const handleClear = () => {
    setFilters({});
    setProcessedFilters(undefined);
    setExtensionFilters([]);
    setStartDate(null);
    setEndDate(null);
    setDocumentTypeFilters([]);
    setThemeFilters([]);
    setHasKeyFactsFilter(undefined);
    setHasRelevantFactsFilter(undefined);
    handleClose();
  };

  return (
    <FilterModalContent
      title="Filter Documents"
      description="Narrow down your document search"
      onApply={handleSubmit}
      onClear={handleClear}
      goToPage={goToPage}
    >
      {/* Key Facts Filter */}
      <FilterSwitch
        id="keyfacts-switch"
        label="Contains Key Facts"
        checked={hasKeyFactsFilter === true}
        onCheckedChange={(checked) => {
          setHasKeyFactsFilter(checked ? true : undefined);
        }}
      />

      {/* Relevant Facts Filter */}
      <FilterSwitch
        id="relevantfacts-switch"
        label="Contains Relevant Facts"
        checked={hasRelevantFactsFilter === true}
        onCheckedChange={(checked) => {
          setHasRelevantFactsFilter(checked ? true : undefined);
        }}
      />

      <div>
        <Label className="mb-2 block">Document Type</Label>
        <DocumentTypeFilter
          selectedTypes={documentTypeFilters}
          setSelectedTypes={(types) => {
            setDocumentTypeFilters(types);
          }}
          allDocumentTypes={allDocumentTypes}
        />
      </div>

      <div>
        <Label className="mb-2 block">Processing Status</Label>
        <Select value={processedFilters} onValueChange={(value) => setProcessedFilters(value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select processing status..." />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="true">Processed Only</SelectItem>
              <SelectItem value="false">Unprocessed Only</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-2 block">Themes</Label>
        <MultiSelect
          values={themeFilters}
          onValuesChange={(values) => {
            setThemeFilters(values);
          }}
        >
          <MultiSelectTrigger className="w-full">
            <MultiSelectValue placeholder="Select themes..." />
          </MultiSelectTrigger>
          <MultiSelectContent>
            <MultiSelectGroup>
              {docThemes.map((theme) => (
                <MultiSelectItem key={theme} value={theme}>
                  {theme}
                </MultiSelectItem>
              ))}
            </MultiSelectGroup>
          </MultiSelectContent>
        </MultiSelect>
      </div>

      <div>
        <Label className="mb-2 block">File Types</Label>
        <MultiSelect values={extensionFilters} onValuesChange={(values) => setExtensionFilters(values)}>
          <MultiSelectTrigger className="w-full">
            <MultiSelectValue placeholder="Select file types..." />
          </MultiSelectTrigger>
          <MultiSelectContent>
            <MultiSelectGroup>
              {fileExtensionOptions.map((fileExtension) => (
                <MultiSelectItem key={fileExtension} value={fileExtension}>
                  {fileExtension}
                </MultiSelectItem>
              ))}
            </MultiSelectGroup>
          </MultiSelectContent>
        </MultiSelect>
      </div>

      <div>
        <Label className="mb-2 block">Document Date Range</Label>
        <DateRangeFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      </div>
    </FilterModalContent>
  );
};
